import { SerializationException } from '../exceptions/serialization-exception.js';
import { SeatingElement } from '../models/serializable/seating/seating-element.js';
import { BinaryPacker } from './binary-packer.js';
import { Seat } from '../models/serializable/seating/seat.js';
import { SeatRow, SeatRowNamingStrategy } from '../models/serializable/seating/seat-row.js';
import { SeatingLabel } from '../models/serializable/seating/seating-label.js';
import { Sector } from '../models/serializable/seating/sector.js';
import { BinaryUnpacker } from './binary-unpacker.js';
import { fromEnumAsInt, fromEnumAsString, toEnumFromInt, toEnumFromString } from './enum-utils.js';
import { Language } from '../models/serializable/language.js';
import { Vec2 } from '../models/math/vec2.js';
import { StandingArea } from '../models/serializable/seating/standing-area.js';

export class SeatingLayoutSerializer {
  static serialize(elements: SeatingElement[]): Uint8Array {
    return this.asBytes(elements);
  }

  static deserialize(bytes: Uint8Array): SeatingElement[] {
    const unpacker = BinaryUnpacker.fromBytes(bytes);
    const format = unpacker.unpackByte();

    switch(format) {
      case 0x01: // Version 1
        return this.fromBytes_v1(unpacker);
      case 0x02: // Version 2
        return this.fromBytes_v2(unpacker);
    }

    throw new SerializationException(`Seating layout cannot be deserialized because format ${format} is unknown`);
  }

  private static asBytes(elements: SeatingElement[]): Uint8Array {
    const packer = new BinaryPacker();

    const packSeatingLabel = (label: SeatingLabel): void => {
      packer.packInt(label.uniqueId);
      packer.packBool(label.enabled);
      packer.packDouble(label.position.x);
      packer.packDouble(label.position.y);
      packer.packDouble(label.rotation);
      packer.packDouble(label.textSize);
      packer.packInt(label.color);

      packer.packLength(label.texts.size);
      for(const [key, value] of label.texts.entries()) {
        packer.packString(fromEnumAsString(key));
        packer.packString(value);
      }
    };

    const packSeat = (seat: Seat): void => {
      packer.packInt(seat.uniqueId);
      packer.packBool(seat.enabled);
      packer.packDouble(seat.position.x);
      packer.packDouble(seat.position.y);
      packer.packDouble(seat.rotation);

      packer.packLength(seat.tickets.length);
      for(let i = 0; i < seat.tickets.length; i++) {
        packer.packString(seat.tickets[i]);
      }
    };

    const packSeatRow = (seatRow: SeatRow): void => {
      packer.packInt(seatRow.uniqueId);
      packer.packBool(seatRow.enabled);
      packer.packDouble(seatRow.position.x);
      packer.packDouble(seatRow.position.y);
      packer.packDouble(seatRow.rotation);
      packer.packInt(fromEnumAsInt(seatRow.namingStrategy));
      packer.packInt(seatRow.firstLabelingOrdinal);

      packer.packBool(seatRow.label != null);
      if(seatRow.label != null) {
        packSeatingLabel(seatRow.label);
      }

      packer.packLength(seatRow.seats.length);
      for(let i = 0; i < seatRow.seats.length; i++) {
        packSeat(seatRow.seats[i]);
      }
    };

    const packSector = (sector: Sector): void => {
      packer.packInt(sector.uniqueId);
      packer.packBool(sector.enabled);
      packer.packDouble(sector.position.x);
      packer.packDouble(sector.position.y);
      packer.packDouble(sector.rotation);

      packer.packBool(sector.label != null);
      if(sector.label != null) {
        packSeatingLabel(sector.label);
      }

      packer.packLength(sector.children.length);
      for(let i = 0; i < sector.children.length; i++) {
        packSeatingElement(sector.children[i]);
      }
    };

    const packStandingArea = (standingArea: StandingArea): void => {
      packer.packInt(standingArea.uniqueId);
      packer.packBool(standingArea.enabled);
      packer.packDouble(standingArea.position.x);
      packer.packDouble(standingArea.position.y);
      packer.packDouble(standingArea.rotation);
      packer.packDouble(standingArea.width);
      packer.packDouble(standingArea.height);
      packer.packLength(standingArea.tickets.length);
      for(let i = 0; i < standingArea.tickets.length; i++) {
        packer.packString(standingArea.tickets[i]);
      }
    };

    const packSeatingElement = (element: SeatingElement): void => {
      const typeId = element.$typeId;
      packer.packByte(typeId);
      switch(typeId) {
        case Seat.TYPE_ID:
          packSeat(element as Seat);
          break;
        case SeatRow.TYPE_ID:
          packSeatRow(element as SeatRow);
          break;
        case SeatingLabel.TYPE_ID:
          packSeatingLabel(element as SeatingLabel);
          break;
        case Sector.TYPE_ID:
          packSector(element as Sector);
          break;
        case StandingArea.TYPE_ID:
          packStandingArea(element as StandingArea);
          break;
        default:
          throw new SerializationException(`Cannot serialize seating layout. Unsupported seating element of type: ${typeId}`);
      }
    };

    packer.packByte(0x02); // Version 1
    packer.packLength(elements.length);

    for(let i = 0; i < elements.length; i++) {
      packSeatingElement(elements[i]);
    }

    return packer.takeBytes();
  }

  private static fromBytes_v2(unpacker: BinaryUnpacker): SeatingElement[] {
    const unpackSeatingLabel = (parent: SeatingElement | null): SeatingLabel => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();
      const textSize = unpacker.unpackDouble();
      const color = unpacker.unpackIntOrNull();

      const numberOfTexts = unpacker.unpackLength();
      const texts = new Map<Language, string>();

      for(let i = 0; i < numberOfTexts; i++) {
        texts.set(
            toEnumFromString(unpacker.unpackString(), Language),
            unpacker.unpackString()
        );
      }

      return new SeatingLabel(
          uniqueId,
          enabled,
          texts,
          textSize,
          color,
          new Vec2(positionX, positionY),
          rotation,
          parent != null ? new WeakRef(parent) : null,
      );
    };

    const unpackSeat = (parent: SeatingElement | null): Seat => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();
      const numberOfTickets = unpacker.unpackLength();

      const tickets: string[] = [];
      for(let i = 0; i < numberOfTickets; i++) {
        tickets.push(unpacker.unpackString());
      }

      return Seat.fromBinaryData(
          uniqueId,
          enabled,
          new Vec2(positionX, positionY),
          rotation,
          tickets,
          parent != null ? new WeakRef(parent) : null,
      );
    };

    const unpackSeatRow = (parent: SeatingElement | null): SeatRow => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();

      const namingStrategy: SeatRowNamingStrategy = toEnumFromInt(unpacker.unpackInt(), SeatRowNamingStrategy);
      const firstLabelingOrdinal: number = unpacker.unpackInt();

      const seatRow = new SeatRow(
          uniqueId,
          enabled,
          new Vec2(positionX, positionY),
          rotation,
          null,
          namingStrategy,
          [],
          firstLabelingOrdinal,
          parent != null ? new WeakRef(parent) : null,
      );

      const hasLabel = unpacker.unpackBool();
      if(hasLabel) {
        seatRow.label = unpackSeatingLabel(seatRow);
      }

      const numberOfSeats = unpacker.unpackLength();
      for(let i = 0; i < numberOfSeats; i++) {
        seatRow.seats.push(unpackSeat(seatRow));
      }

      return seatRow;
    };

    const unpackSector = (parent: SeatingElement | null): Sector => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();

      const sector = new Sector(
          uniqueId,
          enabled,
          new Vec2(positionX, positionY),
          rotation,
          null,
          [],
          parent != null ? new WeakRef(parent) : null,
      );

      const hasLabel = unpacker.unpackBool();
      if(hasLabel) {
        sector.label = unpackSeatingLabel(sector);
      }

      const numberOfElements = unpacker.unpackLength();
      for(let i = 0; i < numberOfElements; i++) {
        sector.children.push(unpackSeatingElement(sector));
      }

      return sector;
    };

    const unpackStandingArea = (parent: SeatingElement | null): StandingArea => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();
      const width = unpacker.unpackDouble();
      const height = unpacker.unpackDouble();
      const numberOfTickets = unpacker.unpackLength();
      const tickets: string[] = [];

      for(let i = 0; i < numberOfTickets; i++) {
        tickets.push(unpacker.unpackString());
      }
      return new StandingArea(
          uniqueId,
          enabled,
          new Vec2(positionX, positionY),
          rotation,
          width,
          height,
          tickets,
          parent != null ? new WeakRef(parent) : null,
      );
    };

    const unpackSeatingElement = (parent: SeatingElement | null): SeatingElement => {
      const typeId = unpacker.unpackByte();
      switch(typeId) {
        case Seat.TYPE_ID:
          return unpackSeat(parent);
        case SeatRow.TYPE_ID:
          return unpackSeatRow(parent);
        case SeatingLabel.TYPE_ID:
          return unpackSeatingLabel(parent);
        case Sector.TYPE_ID:
          return unpackSector(parent);
        case StandingArea.TYPE_ID:
          return unpackStandingArea(parent);
        default:
          throw new SerializationException(`Cannot deserialize seating layout. Unsupported seating element of type: ${typeId}`);
      }
    };

    const numberOfElements = unpacker.unpackLength();
    const elements: SeatingElement[] = [];

    for(let i = 0; i < numberOfElements; i++) {
      elements.push(unpackSeatingElement(null));
    }

    return elements;
  }

  private static fromBytes_v1(unpacker: BinaryUnpacker): SeatingElement[] {
    const unpackSeatingLabel = (parent: SeatingElement | null): SeatingLabel => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();
      const textSize = unpacker.unpackDouble();
      const color = unpacker.unpackIntOrNull();

      const numberOfTexts = unpacker.unpackLength();
      const texts = new Map<Language, string>();

      for(let i = 0; i < numberOfTexts; i++) {
        texts.set(
            toEnumFromString(unpacker.unpackString(), Language),
            unpacker.unpackString()
        );
      }

      return new SeatingLabel(
          uniqueId,
          enabled,
          texts,
          textSize,
          color,
          new Vec2(positionX, positionY),
          rotation,
          parent != null ? new WeakRef(parent) : null,
      );
    };

    const unpackSeat = (parent: SeatingElement | null): Seat => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();
      const ticketTemplateId = unpacker.unpackStringOrNull();

      return Seat.fromBinaryData(
          uniqueId,
          enabled,
          new Vec2(positionX, positionY),
          rotation,
          ticketTemplateId != null ? [ticketTemplateId] : [],
          parent != null ? new WeakRef(parent) : null,
      );
    };

    const unpackSeatRow = (parent: SeatingElement | null): SeatRow => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();

      const namingStrategy: SeatRowNamingStrategy = toEnumFromInt(unpacker.unpackInt(), SeatRowNamingStrategy);
      const firstLabelingOrdinal: number = unpacker.unpackInt();

      const seatRow = new SeatRow(
          uniqueId,
          enabled,
          new Vec2(positionX, positionY),
          rotation,
          null,
          namingStrategy,
          [],
          firstLabelingOrdinal,
          parent != null ? new WeakRef(parent) : null,
      );

      const hasLabel = unpacker.unpackBool();
      if(hasLabel) {
        seatRow.label = unpackSeatingLabel(seatRow);
      }

      const numberOfSeats = unpacker.unpackLength();
      for(let i = 0; i < numberOfSeats; i++) {
        seatRow.seats.push(unpackSeat(seatRow));
      }

      return seatRow;
    };

    const unpackSector = (parent: SeatingElement | null): Sector => {
      const uniqueId = unpacker.unpackInt();
      const enabled = unpacker.unpackBool();
      const positionX = unpacker.unpackDouble();
      const positionY = unpacker.unpackDouble();
      const rotation = unpacker.unpackDouble();

      const sector = new Sector(
          uniqueId,
          enabled,
          new Vec2(positionX, positionY),
          rotation,
          null,
          [],
          parent != null ? new WeakRef(parent) : null,
      );

      const hasLabel = unpacker.unpackBool();
      if(hasLabel) {
        sector.label = unpackSeatingLabel(sector);
      }

      const numberOfElements = unpacker.unpackLength();
      for(let i = 0; i < numberOfElements; i++) {
        sector.children.push(unpackSeatingElement(sector));
      }

      return sector;
    };

    const unpackSeatingElement = (parent: SeatingElement | null): SeatingElement => {
      const typeId = unpacker.unpackByte();
      switch(typeId) {
        case Seat.TYPE_ID:
          return unpackSeat(parent);
        case SeatRow.TYPE_ID:
          return unpackSeatRow(parent);
        case SeatingLabel.TYPE_ID:
          return unpackSeatingLabel(parent);
        case Sector.TYPE_ID:
          return unpackSector(parent);
        default:
          throw new SerializationException(`Cannot deserialize seating layout. Unsupported seating element of type: ${typeId}`);
      }
    };

    const numberOfElements = unpacker.unpackLength();
    const elements: SeatingElement[] = [];

    for(let i = 0; i < numberOfElements; i++) {
      elements.push(unpackSeatingElement(null));
    }

    return elements;
  }
}
